<template>
  <div class="animated fadeIn">
    <b-card
      v-if="!org || !org.accepted"
      class="mt-5 mx-auto py-4 text-center"
      style="max-width: 50rem"
    >
      <template v-if="!org">
        <div class="h3">
          A Nanome Organization allows you to add and delete members, and assign
          roles for each individual in your organization.
        </div>
        <div class="my-1 text-muted" style="font-size: 6rem">
          <i class="icon-organization" />
        </div>
        <b-btn
          v-b-modal.create-org
          :disabled="!canCreateOrg"
          class="px-4"
          variant="primary"
        >
          Create Organization
        </b-btn>
        <div v-if="!canCreateOrg" class="mt-2 small text-muted">
          Free users cannot create an Organization.
        </div>
        <b-modal
          id="create-org"
          ref="modal"
          centered
          hide-footer
          hide-header
          @show="resetForm"
        >
          <h2 class="mb-4">Create Organization</h2>

          <form @submit.prevent="onSubmit">
            <NanInput
              v-model="form.name"
              :validation="$v.form.name"
              label="Organization Name"
              placeholder="Nanome"
              class="mb-3"
              autofocus
            />

            <NanInput
              v-model="form.email"
              :validation="$v.form.email"
              label="Contact Email"
              placeholder="user@nanome.ai"
              type="email"
              class="mb-3"
            />

            <b-btn
              v-b-toggle.billing-info
              class="mb-2 px-0 text-left"
              variant="none"
              block
            >
              Billing Info (optional)
              <i class="icon-arrow-down" />
            </b-btn>
            <b-collapse id="billing-info">
              <NanInput
                v-model="form.billing_info.address"
                label="Address"
                placeholder="123 Main St"
                class="mb-3"
              />

              <b-form-row>
                <b-col>
                  <NanInput
                    v-model="form.billing_info.city"
                    label="City"
                    placeholder="Anytown"
                    class="mb-3"
                  />
                </b-col>
                <b-col>
                  <NanInput
                    v-model="form.billing_info.state"
                    label="State"
                    placeholder="CA"
                    class="mb-3"
                  />
                </b-col>
                <b-col>
                  <NanInput
                    v-model="form.billing_info.zip"
                    label="Zip"
                    placeholder="12345"
                    class="mb-3"
                  />
                </b-col>
                <b-col>
                  <NanInput
                    v-model="form.billing_info.country"
                    label="Country"
                    placeholder="USA"
                    class="mb-3"
                  />
                </b-col>
              </b-form-row>
            </b-collapse>

            <div class="text-right">
              <b-btn
                class="ml-2 px-4"
                variant="secondary"
                @click="$bvModal.hide('create-org')"
              >
                Cancel
              </b-btn>
              <b-btn
                :class="{ disabled: $v.$invalid }"
                :disabled="loading"
                class="ml-2 px-4"
                variant="primary"
                type="submit"
              >
                Submit
              </b-btn>
            </div>
          </form>
        </b-modal>
      </template>

      <template v-else-if="!org.accepted">
        <div class="h3">You have been invited to join the organization</div>
        <h2>{{ org.name }}</h2>
        <div class="my-1 text-muted" style="font-size: 6rem">
          <i class="icon-organization" />
        </div>
        <div>
          <b-btn
            class="mx-1 px-4"
            variant="outline-primary"
            @click="manageInvite(false)"
          >
            Decline
          </b-btn>
          <b-btn
            class="mx-1 px-4"
            variant="primary"
            @click="manageInvite(true)"
          >
            Accept
          </b-btn>
        </div>
      </template>
    </b-card>

    <ManageOrg v-else :org-id="org.id" @updated="reload" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, email } from "vuelidate/lib/validators";
import API from "@/api";
import ManageOrg from "@/components/organizations/ManageOrg";

export default {
  mixins: [validationMixin],

  components: { ManageOrg },

  metaInfo: {
    titleTemplate: "Organization - %s"
  },

  data: () => ({
    form: {
      name: "",
      email: "",
      billing_info: {
        address: "",
        city: "",
        state: "",
        zip: "",
        country: ""
      }
    },
    loading: false
  }),

  computed: {
    ...mapGetters({
      license: "user/activeLicense"
    }),
    ...mapGetters({ org: "user/org" }),

    canCreateOrg() {
      if (!this.license) return false;
      const isPaid = !["free", "trial"].includes(this.license.type);
      const isUpgradedTrial = this.license.upgraded_to;
      return isPaid || isUpgradedTrial;
    }
  },

  methods: {
    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid || this.loading) return;

      const data = { ...this.form };
      if (!Object.values(data.billing_info).some(v => v)) {
        delete data.billing_info;
      }

      this.loading = true;
      const { success } = await API.createOrg(this.form);
      this.loading = false;

      if (success) {
        this.$notify({
          type: "success",
          text: "Organization created"
        });
        this.$refs.modal.hide();
        this.reload();
      }
    },

    async manageInvite(accept) {
      const { success } = await API.manageOrgInvite(this.org.id, accept);
      if (!success) return;

      this.$notify({
        type: "success",
        text: `${this.org.name} invitation ${accept ? "accepted" : "declined"}`
      });

      if (accept) {
        this.$store.commit("user/PATCH_USER", {
          organization: { ...this.org, accepted: true }
        });
      } else {
        this.$store.commit("user/PATCH_USER", { organization: null });
      }
    },

    resetForm() {
      this.form.name = "";
      this.form.email = "";
      this.$v.$reset();
    },

    reload() {
      setTimeout(() => location.reload(), 1000);
    }
  },

  validations: {
    form: {
      name: { required },
      email: { required, email }
    }
  }
};
</script>
